import React, { useState, useEffect, useRef } from 'react';
import '../styles/GHLEventTrigger.css';
import { Dropdown, IconButton, Button, Badge } from './';
import { cloudFunctionV2Auth, checkOnAuthStateChanged, getDocument } from '../functions';
import { HugeiconsIcon } from '@hugeicons/react';
import { Edit02Icon, PlusSignIcon, Delete02Icon } from '@hugeicons/core-free-icons';
import { Exit } from "../assets/icons";
import { colour } from '../styles/GlobalStyles';

function GHLEventTrigger(props) {
    const { 
        companyId: propCompanyId, 
        selectedEvents, 
        onEventsChange,
        selectedTags,
        onTagsChange,
        opportunityCreatedPipelines,
        onOpportunityCreatedPipelinesChange,
        opportunityCreatedStages,
        onOpportunityCreatedStagesChange,
        opportunityStageUpdatedPipelines,
        onOpportunityStageUpdatedPipelinesChange,
        opportunityStageUpdatedStages,
        onOpportunityStageUpdatedStagesChange,
        onSave,
        stageDropdownOpen: propStageDropdownOpen,
        onStageDropdownOpenChange
    } = props;
    
    const [companyId, setCompanyId] = useState(propCompanyId || null);
    const [pipelinesLoading, setPipelinesLoading] = useState(false);
    const [pipelines, setPipelines] = useState([]);
    const [pipelinesError, setPipelinesError] = useState(null);
    
    // State for stage dropdowns - track which dropdown is open per pipeline per event type
    const [opportunityCreatedStageDropdowns, setOpportunityCreatedStageDropdowns] = useState({});
    const [opportunityStageUpdatedStageDropdowns, setOpportunityStageUpdatedStageDropdowns] = useState({});
    
    // State for edit mode dropdowns
    const [editTriggerDropdowns, setEditTriggerDropdowns] = useState({});
    const [editPipelineDropdowns, setEditPipelineDropdowns] = useState({});
    const [editTagsDropdowns, setEditTagsDropdowns] = useState({});
    const [addTriggerDropdownOpen, setAddTriggerDropdownOpen] = useState(false);
    
    // Ref for the edit container to detect clicks outside
    const editContainerRef = useRef(null);
    
    // State for Contact tag updated dropdown
    // Note: tagsDropdownOpen is managed via prop, no local state needed
    const [tagsLoading, setTagsLoading] = useState(false);
    const [tagsError, setTagsError] = useState(null);
    const [availableTags, setAvailableTags] = useState([]);
    
    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    
    // Loading state to track when initial data loading is complete
    const [initialLoading, setInitialLoading] = useState(true);
    
    // State for tracking initial values when edit mode opens
    const [initialEvents, setInitialEvents] = useState(null);
    const [initialTags, setInitialTags] = useState(null);
    const [initialOpportunityCreatedPipelines, setInitialOpportunityCreatedPipelines] = useState(null);
    const [initialOpportunityCreatedStages, setInitialOpportunityCreatedStages] = useState(null);
    const [initialOpportunityStageUpdatedPipelines, setInitialOpportunityStageUpdatedPipelines] = useState(null);
    const [initialOpportunityStageUpdatedStages, setInitialOpportunityStageUpdatedStages] = useState(null);
    
    // Helper function to deep copy arrays
    const deepCopyArray = (arr) => {
        if (!arr) return null;
        return JSON.parse(JSON.stringify(arr));
    };
    
    // Snapshot current state when entering edit mode
    const snapshotCurrentState = () => {
        setInitialEvents(deepCopyArray(selectedEvents));
        setInitialTags(deepCopyArray(selectedTags));
        setInitialOpportunityCreatedPipelines(deepCopyArray(opportunityCreatedPipelines));
        setInitialOpportunityCreatedStages(deepCopyArray(opportunityCreatedStages));
        setInitialOpportunityStageUpdatedPipelines(deepCopyArray(opportunityStageUpdatedPipelines));
        setInitialOpportunityStageUpdatedStages(deepCopyArray(opportunityStageUpdatedStages));
    };
    
    // Check if Save button should be shown for a specific event
    const shouldShowSaveButton = (eventValue) => {
        return eventValue === 'ContactTagUpdated' || 
               eventValue === 'OpportunityCreated' || 
               eventValue === 'OpportunityStageUpdated';
    };
    
    // Check if Save button should be enabled for a specific event
    const isSaveButtonEnabled = (eventValue) => {
        if (eventValue === 'ContactTagUpdated') {
            // Enabled when at least one tag is selected
            return selectedTags && Array.isArray(selectedTags) && selectedTags.length > 0;
        } else if (eventValue === 'OpportunityCreated') {
            // Enabled when at least one pipeline and stage are selected
            const hasPipelines = opportunityCreatedPipelines && opportunityCreatedPipelines.length > 0;
            const hasStages = opportunityCreatedStages && opportunityCreatedStages.length > 0;
            return hasPipelines && hasStages;
        } else if (eventValue === 'OpportunityStageUpdated') {
            // Enabled when at least one pipeline and stage are selected
            const hasPipelines = opportunityStageUpdatedPipelines && opportunityStageUpdatedPipelines.length > 0;
            const hasStages = opportunityStageUpdatedStages && opportunityStageUpdatedStages.length > 0;
            return hasPipelines && hasStages;
        }
        return false;
    };
    
    // Auto-save function for ContactCreated and ContactUpdated
    const autoSaveContactEvents = (events) => {
        if (onSave) {
            // For auto-save, use current state values
            onSave(
                events,
                selectedTags,
                opportunityCreatedPipelines,
                opportunityCreatedStages,
                opportunityStageUpdatedPipelines,
                opportunityStageUpdatedStages
            );
        }
    };
    
    // Revert all changes back to initial state
    const revertChanges = () => {
        if (!initialEvents) return; // No snapshot to revert to
        
        // Restore all state values
        if (initialEvents !== null) {
            onEventsChange(deepCopyArray(initialEvents));
        }
        if (initialTags !== null) {
            onTagsChange(deepCopyArray(initialTags));
        }
        if (initialOpportunityCreatedPipelines !== null) {
            onOpportunityCreatedPipelinesChange(deepCopyArray(initialOpportunityCreatedPipelines));
        }
        if (initialOpportunityCreatedStages !== null) {
            onOpportunityCreatedStagesChange(deepCopyArray(initialOpportunityCreatedStages));
        }
        if (initialOpportunityStageUpdatedPipelines !== null) {
            onOpportunityStageUpdatedPipelinesChange(deepCopyArray(initialOpportunityStageUpdatedPipelines));
        }
        if (initialOpportunityStageUpdatedStages !== null) {
            onOpportunityStageUpdatedStagesChange(deepCopyArray(initialOpportunityStageUpdatedStages));
        }
        
        // Update availableTags to reflect reverted tag selections
        if (availableTags.length > 0 && initialTags) {
            const selectedTagIds = Array.isArray(initialTags) ? initialTags : [];
            setAvailableTags(prev => prev.map(tag => ({
                ...tag,
                selected: selectedTagIds.includes(tag.id)
            })));
        }
    };
    
    const toggleEditMode = () => {
        const willEnterEditMode = !editMode;
        setEditMode(prev => !prev);
        
        if (willEnterEditMode) {
            // Entering edit mode - snapshot current state
            snapshotCurrentState();
        } else {
            // Exiting edit mode - clear snapshots (optional cleanup)
            setInitialEvents(null);
            setInitialTags(null);
            setInitialOpportunityCreatedPipelines(null);
            setInitialOpportunityCreatedStages(null);
            setInitialOpportunityStageUpdatedPipelines(null);
            setInitialOpportunityStageUpdatedStages(null);
        }
    };

    // Helper function to format event name for display
    const getEventDisplayName = (eventValue) => {
        const option = eventOptions.find(opt => opt.value === eventValue);
        return option ? option.label : eventValue;
    };

    // Helper function to format pipeline names from IDs
    const getPipelineNames = (pipelineIds) => {
        if (!Array.isArray(pipelineIds) || pipelineIds.length === 0 || pipelines.length === 0) {
            return [];
        }
        return pipelineIds
            .map(id => {
                const pipeline = pipelines.find(p => p.id === id);
                return pipeline ? pipeline.name : null;
            })
            .filter(name => name !== null);
    };

    // Helper function to format stage names from IDs (with pipeline names)
    const getStageDisplayNames = (stageIds, pipelineIds) => {
        if (!Array.isArray(stageIds) || stageIds.length === 0 || pipelines.length === 0) {
            return [];
        }
        if (!Array.isArray(pipelineIds) || pipelineIds.length === 0) {
            return [];
        }
        
        const stageDisplayNames = [];
        stageIds.forEach(stageId => {
            // Find which pipeline contains this stage
            for (const pipelineId of pipelineIds) {
                const pipeline = pipelines.find(p => p.id === pipelineId);
                if (pipeline && pipeline.stages) {
                    const stage = pipeline.stages.find(s => s.id === stageId);
                    if (stage) {
                        stageDisplayNames.push(`${pipeline.name} (${stage.name})`);
                        break;
                    }
                }
            }
        });
        
        return stageDisplayNames;
    };

    // Event options
    const eventOptions = [
        { value: 'ContactCreated', label: 'Contact created' },
        { value: 'ContactUpdated', label: 'Contact updated' },
        { value: 'ContactTagUpdated', label: 'Contact tag updated' },
        { value: 'OpportunityCreated', label: 'Opportunity created' },
        { value: 'OpportunityStageUpdated', label: 'Opportunity stage updated' }
    ];

    // Check if specific events are selected
    const hasContactTagUpdated = selectedEvents?.includes('ContactTagUpdated');
    const hasOpportunityCreated = selectedEvents?.includes('OpportunityCreated');
    const hasOpportunityStageUpdated = selectedEvents?.includes('OpportunityStageUpdated');
    const hasAnyOpportunityEvent = hasOpportunityCreated || hasOpportunityStageUpdated;

    // Sync stage dropdowns with prop when it changes (when ExitScreen closes)
    useEffect(() => {
        if (propStageDropdownOpen === false) {
            // Close all stage dropdowns
            setOpportunityCreatedStageDropdowns({});
            setOpportunityStageUpdatedStageDropdowns({});
        }
    }, [propStageDropdownOpen]);

    // Get companyId if not provided
    useEffect(() => {
        if (!companyId) {
            const fetchCompanyId = async () => {
                const user = await checkOnAuthStateChanged();
                if (user.status === 200 && user.companyId) {
                    setCompanyId(user.companyId);
                    // Loading will complete in loadSavedSelections
                } else {
                    // No companyId found, loading complete (can't check for triggers)
                    setInitialLoading(false);
                }
            };
            fetchCompanyId();
        }
        // If companyId is provided as prop, loadSavedSelections will handle initialLoading
    }, [companyId]);

    // Load saved selections from ghl_triggers when companyId is available
    // Only load if ghl_accounts exists (GHL integration is set up)
    useEffect(() => {
        if (companyId) {
            const loadSavedSelections = async () => {
                try {
                // First check if ghl_accounts exists
                const accountsColRef = "ghl_accounts";
                const accountsData = await getDocument(accountsColRef, companyId);
                if (accountsData.status === 200) {
                    // GHL account exists, now load triggers
                    const triggersColRef = "ghl_triggers";
                    const triggersData = await getDocument(triggersColRef, companyId);
                    if (triggersData.status === 200) {
                        // Ensure triggersArray is actually an array
                        const rawData = triggersData.data.data;
                        const triggersArray = Array.isArray(rawData) ? rawData : [];
                        
                        // Extract events and their associated data
                        const events = [];
                        triggersArray.forEach(trigger => {
                            if (trigger.event) {
                                events.push(trigger.event);
                                
                                // Load Contact tag updated tags
                                if (trigger.event === 'ContactTagUpdated' && Array.isArray(trigger.tags)) {
                                        // For multiselect, use all tags
                                        onTagsChange(trigger.tags);
                                    // Tags will be synced when fetchTags completes
                                }
                                
                                // Load OpportunityCreated pipelines and stages
                                if (trigger.event === 'OpportunityCreated') {
                                    if (Array.isArray(trigger.pipelines)) {
                                        onOpportunityCreatedPipelinesChange(trigger.pipelines);
                                    }
                                    if (Array.isArray(trigger.stages)) {
                                        onOpportunityCreatedStagesChange(trigger.stages);
                                    }
                                }
                                
                                // Load OpportunityStageUpdated pipelines and stages
                                if (trigger.event === 'OpportunityStageUpdated') {
                                    if (Array.isArray(trigger.pipelines)) {
                                        onOpportunityStageUpdatedPipelinesChange(trigger.pipelines);
                                    }
                                    if (Array.isArray(trigger.stages)) {
                                        onOpportunityStageUpdatedStagesChange(trigger.stages);
                                    }
                                }
                            }
                        });
                        
                        // Update parent component with saved events
                        if (events.length > 0) {
                            onEventsChange(events);
                        }
                    }
                        // Mark loading complete after checking triggers (whether found or not)
                        setInitialLoading(false);
                    } else {
                        // No ghl_accounts found, loading complete
                        setInitialLoading(false);
                    }
                } catch (error) {
                    console.error('Error loading saved selections:', error);
                    // Mark loading complete even on error
                    setInitialLoading(false);
                }
            };
            loadSavedSelections();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    // Fetch pipelines when an opportunity event is selected OR when we need them for read-only/edit view
    useEffect(() => {
        const needsPipelines = (hasAnyOpportunityEvent || 
            (selectedEvents && selectedEvents.some(e => e === 'OpportunityCreated' || e === 'OpportunityStageUpdated')) ||
            editMode); // Fetch in edit mode to have them available for dropdowns
        
        if (needsPipelines && companyId && pipelines.length === 0 && !pipelinesLoading && !pipelinesError) {
            fetchPipelines();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAnyOpportunityEvent, companyId, selectedEvents, editMode]);

    // Fetch tags when Contact tag updated is selected
    useEffect(() => {
        if (hasContactTagUpdated && companyId && availableTags.length === 0 && !tagsLoading && !tagsError) {
            fetchTags();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasContactTagUpdated, companyId]);

    // Sync availableTags selection state when selectedTags prop changes
    useEffect(() => {
        if (availableTags.length > 0 && selectedTags) {
            // For multiselect, selectedTags is an array of tag IDs
            const selectedTagIds = Array.isArray(selectedTags) ? selectedTags : [];
            setAvailableTags(prev => prev.map(tag => ({
                ...tag,
                selected: selectedTagIds.includes(tag.id)
            })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTags]);

    // Close dropdowns when clicking outside in edit mode
    useEffect(() => {
        if (!editMode) return;

        const handleClickOutside = (event) => {
            // Check if any dropdown is open
            const hasOpenTriggerDropdown = Object.values(editTriggerDropdowns).some(open => open === true);
            const hasOpenPipelineDropdown = Object.values(editPipelineDropdowns).some(open => open === true);
            const hasOpenStageDropdown = Object.values(opportunityCreatedStageDropdowns).some(open => open === true) ||
                                        Object.values(opportunityStageUpdatedStageDropdowns).some(open => open === true);
            const hasOpenTagsDropdown = Object.values(editTagsDropdowns).some(open => open === true);
            
            const anyDropdownOpen = hasOpenTriggerDropdown || hasOpenPipelineDropdown || hasOpenStageDropdown || hasOpenTagsDropdown || addTriggerDropdownOpen;
            
            if (!anyDropdownOpen) return; // No dropdowns open, nothing to close
            
            // Check if click is on a dropdown element
            const target = event.target;
            const isDropdownElement = target.closest('.dropdown-outer-container') || 
                                      target.closest('.dropdown-options-container');
            
            // If click is outside dropdown elements (even if inside edit container), close all dropdowns
            if (!isDropdownElement) {
                setEditTriggerDropdowns({});
                setEditPipelineDropdowns({});
                setOpportunityCreatedStageDropdowns({});
                setOpportunityStageUpdatedStageDropdowns({});
                setEditTagsDropdowns({});
                setAddTriggerDropdownOpen(false);
            }
        };

        // Add event listener
        document.addEventListener('mousedown', handleClickOutside);
        
        // Cleanup
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editMode, editTriggerDropdowns, editPipelineDropdowns, opportunityCreatedStageDropdowns, opportunityStageUpdatedStageDropdowns, editTagsDropdowns, addTriggerDropdownOpen]);

    const fetchPipelines = async () => {
        if (!companyId) {
            setPipelinesError('Company ID is required');
            return;
        }

        setPipelinesLoading(true);
        setPipelinesError(null);

        try {
            const url = process.env.REACT_APP_GET_GHL_OPPORTUNITIES;
            const params = {
                companyId: companyId
            };
            const result = await cloudFunctionV2Auth(url, params);

            if (result.status === 200 && result.data) {
                // Extract pipelines from the response
                const pipelinesData = result.data.pipelines || result.data.data?.pipelines || result.data;
                
                if (Array.isArray(pipelinesData)) {
                    const formattedPipelines = pipelinesData.map(pipeline => ({
                        id: pipeline.id,
                        name: pipeline.name || 'Unnamed Pipeline',
                        stages: (Array.isArray(pipeline.stages) ? pipeline.stages : []).map(stage => ({
                            id: stage.id,
                            name: stage.name || 'Unnamed Stage',
                            position: stage.position || 0
                        }))
                    }));
                    setPipelines(formattedPipelines);
                    
                    // Ensure each selected pipeline has at least one stage selected
                    // Check OpportunityCreated pipelines
                    if (opportunityCreatedPipelines && opportunityCreatedPipelines.length > 0) {
                        const stagesToAdd = [];
                        opportunityCreatedPipelines.forEach(pipelineId => {
                            const pipeline = formattedPipelines.find(p => p.id === pipelineId);
                            if (pipeline && pipeline.stages && pipeline.stages.length > 0) {
                                const pipelineStageIds = pipeline.stages.map(s => s.id);
                                const hasStagesFromThisPipeline = opportunityCreatedStages?.some(stageId => pipelineStageIds.includes(stageId));
                                
                                if (!hasStagesFromThisPipeline) {
                                    // Auto-select the first stage (sorted by position)
                                    const sortedStages = [...pipeline.stages].sort((a, b) => (a.position || 0) - (b.position || 0));
                                    stagesToAdd.push(sortedStages[0].id);
                                }
                            }
                        });
                        
                        if (stagesToAdd.length > 0) {
                            const newStages = [...(opportunityCreatedStages || []), ...stagesToAdd];
                            onOpportunityCreatedStagesChange(newStages);
                        }
                    }
                    
                    // Check OpportunityStageUpdated pipelines
                    if (opportunityStageUpdatedPipelines && opportunityStageUpdatedPipelines.length > 0) {
                        const stagesToAdd = [];
                        opportunityStageUpdatedPipelines.forEach(pipelineId => {
                            const pipeline = formattedPipelines.find(p => p.id === pipelineId);
                            if (pipeline && pipeline.stages && pipeline.stages.length > 0) {
                                const pipelineStageIds = pipeline.stages.map(s => s.id);
                                const hasStagesFromThisPipeline = opportunityStageUpdatedStages?.some(stageId => pipelineStageIds.includes(stageId));
                                
                                if (!hasStagesFromThisPipeline) {
                                    // Auto-select the first stage (sorted by position)
                                    const sortedStages = [...pipeline.stages].sort((a, b) => (a.position || 0) - (b.position || 0));
                                    stagesToAdd.push(sortedStages[0].id);
                                }
                            }
                        });
                        
                        if (stagesToAdd.length > 0) {
                            const newStages = [...(opportunityStageUpdatedStages || []), ...stagesToAdd];
                            onOpportunityStageUpdatedStagesChange(newStages);
                        }
                    }
                } else {
                    setPipelinesError('No pipelines found');
                }
            } else {
                // Handle error
                let errorMessage = 'Failed to fetch pipelines';
                if (result.error) {
                    if (typeof result.error === 'string') {
                        errorMessage = result.error;
                    } else if (result.error.message) {
                        errorMessage = result.error.message;
                    } else if (result.error.error) {
                        errorMessage = typeof result.error.error === 'string' 
                            ? result.error.error 
                            : result.error.error?.message || errorMessage;
                    } else {
                        errorMessage = JSON.stringify(result.error);
                    }
                }
                setPipelinesError(errorMessage);
            }
        } catch (error) {
            console.error('Error fetching pipelines:', error);
            setPipelinesError('Failed to fetch pipelines');
        } finally {
            setPipelinesLoading(false);
        }
    };

    const fetchTags = async () => {
        if (!companyId) {
            setTagsError('Company ID is required');
            return;
        }

        setTagsLoading(true);
        setTagsError(null);

        try {
            const url = process.env.REACT_APP_GET_GHL_TAGS;
            const params = {
                companyId: companyId
            };
            const result = await cloudFunctionV2Auth(url, params);

            if (result.status === 200 && result.data) {
                // Extract tags from the response
                // Structure may vary: { tags: [{ id, name }] } or { data: { tags: [...] } }
                const tagsData = result.data.tags || result.data.data?.tags || result.data;
                
                if (Array.isArray(tagsData)) {
                    // Format tags for Dropdown component.
                    // Key triggers by tag NAME, not ID: GHL's ContactTagUpdate
                    // webhook delivers tag names (not IDs), so a name-keyed trigger
                    // is what the backend can actually match against.
                    const selectedTagIds = Array.isArray(selectedTags) ? selectedTags : [];
                    const formattedTags = tagsData.map(tag => {
                        const tagKey = tag.name || tag.label;
                        return {
                            label: tag.name || tag.label || 'Unnamed Tag',
                            id: tagKey,
                            selected: selectedTagIds.includes(tagKey)
                        };
                    });
                    setAvailableTags(formattedTags);
                } else {
                    setTagsError('No tags found');
                }
            } else {
                // Handle error
                let errorMessage = 'Failed to fetch tags';
                if (result.error) {
                    if (typeof result.error === 'string') {
                        errorMessage = result.error;
                    } else if (result.error.message) {
                        errorMessage = result.error.message;
                    } else if (result.error.error) {
                        errorMessage = typeof result.error.error === 'string' 
                            ? result.error.error 
                            : result.error.error?.message || errorMessage;
                    } else {
                        errorMessage = JSON.stringify(result.error);
                    }
                }
                setTagsError(errorMessage);
            }
        } catch (error) {
            console.error('Error fetching tags:', error);
            setTagsError('Failed to fetch tags');
        } finally {
            setTagsLoading(false);
        }
    };


    const handleTagSelect = (tagOption, index) => {
        // Multiselect: toggle the tag selection
        // Find the tag in availableTags by ID
        const tagIndex = availableTags.findIndex(t => t.id === tagOption.id);
        if (tagIndex === -1) return;
        
        const newTags = availableTags.map((t, i) => ({
            ...t,
            selected: i === tagIndex ? !t.selected : t.selected
        }));
        setAvailableTags(newTags);
        
        // Use tag IDs for saving (not labels) - store as array of selected tag IDs
        const selectedTagIds = newTags
            .filter(t => t.selected)
            .map(t => t.id);
        onTagsChange(selectedTagIds);
    };
    
    const handleTagRemove = (tagId) => {
        // Remove a tag from selection
        const newTags = availableTags.map(tag => ({
            ...tag,
            selected: tag.id === tagId ? false : tag.selected
        }));
        setAvailableTags(newTags);
        
        const selectedTagIds = newTags
            .filter(t => t.selected)
            .map(t => t.id);
        onTagsChange(selectedTagIds);
    };

    const handlePipelineSelect = (eventType, pipelineId) => {
        if (eventType === 'OpportunityCreated') {
            const isCurrentlySelected = opportunityCreatedPipelines?.includes(pipelineId);
            const newPipelines = isCurrentlySelected
                ? opportunityCreatedPipelines.filter(id => id !== pipelineId)
                : [...(opportunityCreatedPipelines || []), pipelineId];
            
            onOpportunityCreatedPipelinesChange(newPipelines);
            
            let newStages = opportunityCreatedStages;
            
            if (isCurrentlySelected) {
                // If deselecting pipeline, remove all its stages from selection
                const pipeline = pipelines.find(p => p.id === pipelineId);
                if (pipeline && pipeline.stages) {
                    const stageIds = pipeline.stages.map(s => s.id);
                    newStages = opportunityCreatedStages?.filter(id => !stageIds.includes(id)) || [];
                    onOpportunityCreatedStagesChange(newStages);
                }
            } else {
                // If selecting pipeline, auto-select first stage if no stages from this pipeline are selected
                const pipeline = pipelines.find(p => p.id === pipelineId);
                if (pipeline && pipeline.stages && pipeline.stages.length > 0) {
                    const pipelineStageIds = pipeline.stages.map(s => s.id);
                    const hasStagesFromThisPipeline = opportunityCreatedStages?.some(stageId => pipelineStageIds.includes(stageId));
                    
                    if (!hasStagesFromThisPipeline) {
                        // Auto-select the first stage (sorted by position)
                        const sortedStages = [...pipeline.stages].sort((a, b) => (a.position || 0) - (b.position || 0));
                        const firstStageId = sortedStages[0].id;
                        newStages = [...(opportunityCreatedStages || []), firstStageId];
                        onOpportunityCreatedStagesChange(newStages);
                        return; // Early return
                    }
                }
            }
        } else if (eventType === 'OpportunityStageUpdated') {
            const isCurrentlySelected = opportunityStageUpdatedPipelines?.includes(pipelineId);
            const newPipelines = isCurrentlySelected
                ? opportunityStageUpdatedPipelines.filter(id => id !== pipelineId)
                : [...(opportunityStageUpdatedPipelines || []), pipelineId];
            
            onOpportunityStageUpdatedPipelinesChange(newPipelines);
            
            let newStages = opportunityStageUpdatedStages;
            
            if (isCurrentlySelected) {
                // If deselecting pipeline, remove all its stages from selection
                const pipeline = pipelines.find(p => p.id === pipelineId);
                if (pipeline && pipeline.stages) {
                    const stageIds = pipeline.stages.map(s => s.id);
                    newStages = opportunityStageUpdatedStages?.filter(id => !stageIds.includes(id)) || [];
                    onOpportunityStageUpdatedStagesChange(newStages);
                }
            } else {
                // If selecting pipeline, auto-select first stage if no stages from this pipeline are selected
                const pipeline = pipelines.find(p => p.id === pipelineId);
                if (pipeline && pipeline.stages && pipeline.stages.length > 0) {
                    const pipelineStageIds = pipeline.stages.map(s => s.id);
                    const hasStagesFromThisPipeline = opportunityStageUpdatedStages?.some(stageId => pipelineStageIds.includes(stageId));
                    
                    if (!hasStagesFromThisPipeline) {
                        // Auto-select the first stage (sorted by position)
                        const sortedStages = [...pipeline.stages].sort((a, b) => (a.position || 0) - (b.position || 0));
                        const firstStageId = sortedStages[0].id;
                        newStages = [...(opportunityStageUpdatedStages || []), firstStageId];
                        onOpportunityStageUpdatedStagesChange(newStages);
                        return; // Early return
                    }
                }
            }
        }
    };

    const handleStageSelect = (eventType, pipelineId, stageId) => {
        if (eventType === 'OpportunityCreated') {
            // Single selection: replace any existing stage for this pipeline with the new one
            const pipeline = pipelines.find(p => p.id === pipelineId);
            if (pipeline && pipeline.stages) {
                const pipelineStageIds = pipeline.stages.map(s => s.id);
                // Remove all stages from this pipeline, then add the selected one
                const newStages = (opportunityCreatedStages || []).filter(id => !pipelineStageIds.includes(id));
                newStages.push(stageId);
                onOpportunityCreatedStagesChange(newStages);
            }
        } else if (eventType === 'OpportunityStageUpdated') {
            // Single selection: replace any existing stage for this pipeline with the new one
            const pipeline = pipelines.find(p => p.id === pipelineId);
            if (pipeline && pipeline.stages) {
                const pipelineStageIds = pipeline.stages.map(s => s.id);
                // Remove all stages from this pipeline, then add the selected one
                const newStages = (opportunityStageUpdatedStages || []).filter(id => !pipelineStageIds.includes(id));
                newStages.push(stageId);
                onOpportunityStageUpdatedStagesChange(newStages);
            }
        }
    };

    const toggleStageDropdown = (eventType, pipelineId) => {
        const isCurrentlyOpen = eventType === 'OpportunityCreated' 
            ? opportunityCreatedStageDropdowns[pipelineId] || false
            : opportunityStageUpdatedStageDropdowns[pipelineId] || false;
        
        const newState = !isCurrentlyOpen;
        
        let updatedCreatedDropdowns = opportunityCreatedStageDropdowns;
        let updatedUpdatedDropdowns = opportunityStageUpdatedStageDropdowns;
        
        if (eventType === 'OpportunityCreated') {
            updatedCreatedDropdowns = {
                ...opportunityCreatedStageDropdowns,
                [pipelineId]: newState
            };
            setOpportunityCreatedStageDropdowns(updatedCreatedDropdowns);
        } else if (eventType === 'OpportunityStageUpdated') {
            updatedUpdatedDropdowns = {
                ...opportunityStageUpdatedStageDropdowns,
                [pipelineId]: newState
            };
            setOpportunityStageUpdatedStageDropdowns(updatedUpdatedDropdowns);
        }
        
        // Notify parent if any stage dropdown is open
        if (onStageDropdownOpenChange) {
            // Check if any dropdown is open after this toggle
            const anyCreatedOpen = Object.values(updatedCreatedDropdowns).some(open => open === true);
            const anyUpdatedOpen = Object.values(updatedUpdatedDropdowns).some(open => open === true);
            const anyDropdownOpen = anyCreatedOpen || anyUpdatedOpen;
            
            onStageDropdownOpenChange(anyDropdownOpen);
        }
    };

    const getSelectedStageForPipeline = (eventType, pipelineId, selectedStages) => {
        const pipeline = pipelines.find(p => p.id === pipelineId);
        if (pipeline && pipeline.stages) {
            const pipelineStageIds = pipeline.stages.map(s => s.id);
            const selectedStageId = selectedStages?.find(id => pipelineStageIds.includes(id));
            if (selectedStageId) {
                const stage = pipeline.stages.find(s => s.id === selectedStageId);
                return stage ? { id: stage.id, label: stage.name } : null;
            }
        }
        return null;
    };

    // Render zero state (when no triggers and loading is complete).
    // Show the Add trigger dropdown directly — selecting an event enters edit
    // mode so its config shows immediately, avoiding a separate button click.
    const renderZeroState = () => {
        return (
            <div className="ghl-event-trigger-add-trigger-container">
                <Dropdown
                    open={addTriggerDropdownOpen}
                    icon={<HugeiconsIcon icon={PlusSignIcon} color={colour.gray06} size={16} />}
                    label="Add trigger"
                    disabled={!eventOptions || eventOptions.length === 0 || (selectedEvents && selectedEvents.length >= eventOptions.length)}
                    options={eventOptions
                        .filter(opt => !selectedEvents?.includes(opt.value))
                        .map(opt => ({
                            label: opt.label,
                            id: opt.value,
                            selected: false
                        }))}
                    multiselect={false}
                    cta={(option) => {
                        const newEvents = [...(selectedEvents || []), option.id];
                        // Enter edit mode (snapshots the empty state) so the new
                        // trigger's config UI shows right away.
                        if (!editMode) toggleEditMode();
                        onEventsChange(newEvents);
                        setAddTriggerDropdownOpen(false);
                        // Auto-save ContactCreated and ContactUpdated
                        if (option.id === 'ContactCreated' || option.id === 'ContactUpdated') {
                            autoSaveContactEvents(newEvents);
                        }
                    }}
                    toggleDropdown={() => {
                        setAddTriggerDropdownOpen(prev => !prev);
                    }}
                    sidealign="left"
                    topalign="bottom"
                    indexSelect={false}
                    selectedIndex={null}
                />
            </div>
        );
    };

    // Render read-only view for configured triggers
    const renderReadOnlyView = () => {
        // Show zero state only when loading is complete and there are no triggers
        if (initialLoading === false && (!selectedEvents || selectedEvents.length === 0)) {
            return renderZeroState();
        }
        
        // Show loading or empty message while still loading
        if (initialLoading || !selectedEvents || selectedEvents.length === 0) {
            return null; // Return null while loading, zero state will show when loading completes
        }

        return (
            <div className="ghl-event-trigger-readonly-list">
                {selectedEvents.map((eventValue, index) => {
                    const isOpportunityEvent = eventValue === 'OpportunityCreated' || eventValue === 'OpportunityStageUpdated';
                    const isContactTagUpdated = eventValue === 'ContactTagUpdated';
                    const eventDisplayName = getEventDisplayName(eventValue);
                    
                    // Get pipeline and stage data for opportunity events
                    let pipelineNames = [];
                    let stageDisplayNames = [];
                    
                    // Get tag names for ContactTagUpdated
                    let tagNames = [];
                    if (isContactTagUpdated && selectedTags && Array.isArray(selectedTags)) {
                        tagNames = selectedTags
                            .map(tagId => {
                                const tag = availableTags.find(t => t.id === tagId);
                                // Stored value is the tag name, so fall back to it
                                // when availableTags hasn't loaded or the tag was
                                // removed in GHL — rather than hiding the trigger.
                                return tag ? tag.label : tagId;
                            })
                            .filter(name => name !== null && name !== undefined && name !== '');
                    }
                    
                    if (eventValue === 'OpportunityCreated') {
                        pipelineNames = getPipelineNames(opportunityCreatedPipelines);
                        stageDisplayNames = getStageDisplayNames(opportunityCreatedStages, opportunityCreatedPipelines);
                    } else if (eventValue === 'OpportunityStageUpdated') {
                        pipelineNames = getPipelineNames(opportunityStageUpdatedPipelines);
                        stageDisplayNames = getStageDisplayNames(opportunityStageUpdatedStages, opportunityStageUpdatedPipelines);
                    }

                    return (
                        <div key={`trigger-${eventValue}-${index}`} className="ghl-event-trigger-readonly-container relative-container">
                            <IconButton
                                size="medium"
                                icon={<HugeiconsIcon icon={Edit02Icon} color={colour.gray06} size={14} className="ghl-event-trigger-edit-icon" />}
                                disabled={false}
                                clickFunc={toggleEditMode}
                                standardIcon={false}
                                background={true}
                                bgType="white"
                                contextTooltip={false}
                                contextTooltipBG={null}
                                contextTooltipText={null}
                                customClass={"ghl-event-trigger-edit-button"}
                            />
                            
                            <div className="ghl-event-trigger-readonly-block">
                                <span className="body-bold colour-primary">Trigger</span>
                                <span className="body-regular colour-primary">{eventDisplayName}</span>
                            </div>
                            
                            {isOpportunityEvent && (
                                <>
                                    <div className="ghl-event-trigger-readonly-block">
                                        <span className="body-bold colour-primary">Pipeline</span>
                                        {pipelineNames.length > 0 ? (
                                            <span className="body-regular colour-primary">{pipelineNames.join(', ')}</span>
                                        ) : (
                                            <span className="body-regular colour-primary">No pipeline selected</span>
                                        )}
                                    </div>
                                    
                                    <div className="ghl-event-trigger-readonly-block">
                                        <span className="body-bold colour-primary">Stage</span>
                                        {stageDisplayNames.length > 0 ? (
                                            <span className="body-regular colour-primary">{stageDisplayNames.join(', ')}</span>
                                        ) : (
                                            <span className="body-regular colour-primary">No stage selected</span>
                                        )}
                                    </div>
                                </>
                            )}
                            
                            {isContactTagUpdated && (
                                <div className="ghl-event-trigger-readonly-block">
                                    <span className="body-bold colour-primary">Tags</span>
                                    {tagNames.length > 0 ? (
                                        <span className="body-regular colour-primary">{tagNames.join(', ')}</span>
                                    ) : (
                                        <span className="body-regular colour-primary">No tags selected</span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };


    // Render edit view with dropdowns for each trigger
    const renderEditView = () => {
        const triggers = selectedEvents || [];

    return (
            <div ref={editContainerRef} className="ghl-event-trigger-edit-view-wrapper">
                {triggers.map((eventValue, triggerIndex) => {
                    const isOpportunityEvent = eventValue === 'OpportunityCreated' || eventValue === 'OpportunityStageUpdated';
                    const isContactTagUpdated = eventValue === 'ContactTagUpdated';
                    const eventLabel = getEventDisplayName(eventValue);
                    
                    // Get pipeline and stage data for opportunity events
                    let pipelineIds = [];
                    let stageIds = [];
                    let stageDropdowns = {};
                    
                    if (eventValue === 'OpportunityCreated') {
                        pipelineIds = opportunityCreatedPipelines || [];
                        stageIds = opportunityCreatedStages || [];
                        stageDropdowns = opportunityCreatedStageDropdowns || {};
                    } else if (eventValue === 'OpportunityStageUpdated') {
                        pipelineIds = opportunityStageUpdatedPipelines || [];
                        stageIds = opportunityStageUpdatedStages || [];
                        stageDropdowns = opportunityStageUpdatedStageDropdowns || {};
                    }
                    
                    // Get selected tags for ContactTagUpdated
                    const selectedTagIds = isContactTagUpdated && Array.isArray(selectedTags) ? selectedTags : [];
                    const selectedTagsData = selectedTagIds.map(tagId => {
                        const tag = availableTags.find(t => t.id === tagId);
                        return tag ? { id: tag.id, label: tag.label } : null;
                    }).filter(tag => tag !== null);
                    
                    // Format trigger options for dropdown
                    const triggerOptions = eventOptions.map(opt => ({
                        label: opt.label,
                        id: opt.value,
                        selected: opt.value === eventValue
                    }));
                    
                            return (
                        <div key={`edit-trigger-${eventValue}-${triggerIndex}`} className="ghl-event-trigger-edit-container relative-container">
                            <IconButton
                                size="medium"
                                icon={<Exit className="" fill={colour.red03} clickFunc={null} size={14} />}
                                    disabled={false}
                                clickFunc={() => {
                                    revertChanges();
                                    toggleEditMode();
                                }}
                                standardIcon={false}
                                background={true}
                                bgType="destructive"
                                contextTooltip={false}
                                contextTooltipBG={null}
                                contextTooltipText={null}
                                customClass={"ghl-event-trigger-edit-exit-button"}
                            />
                            
                            {/* Trigger dropdown */}
                            <div className="ghl-event-trigger-edit-trigger-dropdown">
                                    <Dropdown
                                    open={editTriggerDropdowns[triggerIndex] || false}
                                        icon={null}
                                    label={eventLabel}
                                        disabled={false}
                                    options={triggerOptions}
                                        multiselect={false}
                                    cta={(option) => {
                                        // Change trigger type - need to handle state migration
                                        const newEvents = selectedEvents.map(e => e === eventValue ? option.id : e);
                                        onEventsChange(newEvents);
                                        // Clear pipeline/stage data when changing from opportunity event
                                        if (isOpportunityEvent && option.id !== 'OpportunityCreated' && option.id !== 'OpportunityStageUpdated') {
                                            if (eventValue === 'OpportunityCreated') {
                                                onOpportunityCreatedPipelinesChange([]);
                                                onOpportunityCreatedStagesChange([]);
                                            } else if (eventValue === 'OpportunityStageUpdated') {
                                                onOpportunityStageUpdatedPipelinesChange([]);
                                                onOpportunityStageUpdatedStagesChange([]);
                                            }
                                        }
                                        // Auto-save if selecting ContactCreated/ContactUpdated OR deselecting them
                                        const wasContactEvent = eventValue === 'ContactCreated' || eventValue === 'ContactUpdated';
                                        const isContactEvent = option.id === 'ContactCreated' || option.id === 'ContactUpdated';
                                        if (wasContactEvent || isContactEvent) {
                                            autoSaveContactEvents(newEvents);
                                        }
                                        // Close dropdown
                                        setEditTriggerDropdowns(prev => ({ ...prev, [triggerIndex]: false }));
                                        }}
                                        toggleDropdown={() => {
                                        setEditTriggerDropdowns(prev => ({ ...prev, [triggerIndex]: !(prev[triggerIndex] || false) }));
                                        }}
                                        sidealign="left"
                                        topalign="bottom"
                                        indexSelect={false}
                                        selectedIndex={null}
                                    />
                                </div>
                            
                            {/* Pipeline and Stage dropdowns for opportunity events */}
                            {isOpportunityEvent && (
                                <div className="ghl-event-trigger-edit-pipelines-section">
                                    <span className="body-bold colour-primary">Pipelines</span>
                                    <div className="ghl-event-trigger-edit-pipelines-list">
                                        {pipelineIds.length > 0 ? (
                                            pipelineIds.map((pipelineId, pipelineIndex) => {
                                                const pipeline = pipelines.find(p => p.id === pipelineId);
                                                const selectedStage = getSelectedStageForPipeline(eventValue, pipelineId, stageIds);
                                                const isDropdownOpen = stageDropdowns[pipelineId] || false;
                                                
                                                // Format pipeline options
                                                const pipelineOptions = pipelines.map(p => ({
                                                    label: p.name,
                                                    id: p.id,
                                                    selected: p.id === pipelineId
                                                }));
                                                
                                                // Format stage options
                                                const stageOptions = pipeline && pipeline.stages
                                                    ? pipeline.stages
                                                        .sort((a, b) => (a.position || 0) - (b.position || 0))
                                                        .map(stage => ({
                                                            label: stage.name,
                                                            id: stage.id,
                                                            selected: selectedStage?.id === stage.id
                                                        }))
                                                    : [];
                                                
                                                return (
                                                    <div key={`pipeline-${pipelineId}-${pipelineIndex}`} className="ghl-event-trigger-edit-pipeline-row">
                                                        <Dropdown
                                                            open={editPipelineDropdowns[`${eventValue}-${pipelineId}`] || false}
                                                            icon={null}
                                                            label={pipeline ? pipeline.name : 'Select pipeline'}
                                                            disabled={pipelinesLoading}
                                                            options={pipelineOptions}
                                                            multiselect={false}
                                                            cta={(option) => {
                                                                handlePipelineSelect(eventValue, option.id);
                                                                // Close dropdown
                                                                setEditPipelineDropdowns(prev => ({ ...prev, [`${eventValue}-${pipelineId}`]: false }));
                                                            }}
                                                            toggleDropdown={() => {
                                                                const key = `${eventValue}-${pipelineId}`;
                                                                setEditPipelineDropdowns(prev => ({ ...prev, [key]: !(prev[key] || false) }));
                                                            }}
                                                            sidealign="left"
                                                            topalign="bottom"
                                                            indexSelect={false}
                                                            selectedIndex={null}
                                                        />
                                                        <Dropdown
                                                            open={isDropdownOpen}
                                                            icon={null}
                                                            label={selectedStage ? selectedStage.label : 'Select stage'}
                                                            disabled={!pipeline || stageOptions.length === 0}
                                                            options={stageOptions}
                                                            multiselect={false}
                                                            cta={(stageOption) => {
                                                                handleStageSelect(eventValue, pipelineId, stageOption.id);
                                                                toggleStageDropdown(eventValue, pipelineId);
                                                            }}
                                                            toggleDropdown={() => toggleStageDropdown(eventValue, pipelineId)}
                                                            sidealign="left"
                                                            topalign="bottom"
                                                            indexSelect={false}
                                                            selectedIndex={null}
                                                        />
                                                    </div>
                                                );
                                            })
                            ) : (
                                <div className="ghl-event-trigger-empty-container">
                                    <span className="body-regular colour-primary">
                                                    No pipelines selected
                                    </span>
                                </div>
                                        )}
                                    </div>
                                    
                                    {pipelinesError && (
                                        <span className="body-regular colour-error">
                                            We couldn't load your pipelines.{' '}
                                            <span className="underline cursor-pointer" onClick={() => fetchPipelines()}>Try again</span>
                                        </span>
                                    )}
                                    {/* Add another pipeline button */}
                                    <span
                                        className={`body-regular colour-primary text-button ghl-event-trigger-add-pipeline-wrapper ${pipelinesLoading || !pipelines || pipelines.length === 0 || pipelineIds.length >= pipelines.length ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (!pipelinesLoading && pipelines && pipelines.length > 0 && pipelineIds.length < pipelines.length) {
                                                // Add first available pipeline that isn't selected
                                                const unselectedPipeline = pipelines.find(p => !pipelineIds.includes(p.id));
                                                if (unselectedPipeline) {
                                                    handlePipelineSelect(eventValue, unselectedPipeline.id);
                                                }
                                            }
                                        }}
                                    >
                                        <HugeiconsIcon icon={PlusSignIcon} color={colour.gray06} size={16} className="ghl-event-trigger-add-pipeline-icon" />
                                        Add another pipeline
                                    </span>
                    </div>
                            )}
                            
                            {/* Tags section for ContactTagUpdated */}
                            {isContactTagUpdated && (
                                <div className="ghl-event-trigger-edit-tags-section">
                                    <span className="body-bold colour-primary">Tags</span>
                                    <Dropdown
                                        open={editTagsDropdowns[triggerIndex] || false}
                                        icon={null}
                                        label={selectedTagsData.length > 0 ? `${selectedTagsData.length} tag${selectedTagsData.length !== 1 ? 's' : ''} selected` : 'Select tags'}
                                        disabled={tagsLoading || availableTags.length === 0}
                                        options={availableTags}
                                        multiselect={true}
                                        cta={(option, index) => {
                                            handleTagSelect(option, index);
                                        }}
                                        toggleDropdown={() => {
                                            setEditTagsDropdowns(prev => ({ ...prev, [triggerIndex]: !(prev[triggerIndex] || false) }));
                                        }}
                                        sidealign="left"
                                        topalign="bottom"
                                        indexSelect={false}
                                        selectedIndex={null}
                                    />
                                    {tagsError && (
                                        <span className="body-regular colour-error">
                                            We couldn't load your tags.{' '}
                                            <span className="underline cursor-pointer" onClick={() => fetchTags()}>Try again</span>
                                        </span>
                                    )}
                                    {selectedTagsData.length > 0 && (
                                        <div className="ghl-event-trigger-edit-tags-list">
                                            {selectedTagsData.map((tag) => (
                                                <Badge
                                                    key={tag.id}
                                                    bgColour="bg-colour-light"
                                                    statusDot={false}
                                                    statusDotColour={null}
                                                    text={tag.label}
                                                    textClass="body-regular colour-primary"
                                                    icon={<Exit className="cursor-pointer" fill={colour.gray06} clickFunc={handleTagRemove.bind(null, tag.id)} size={16} />}
                                                    tooltip={false}
                                                    tooltipBody={null}
                                                    tooltipPosition={null}
                                                    iconLeft={false}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Note: ContactCreated and ContactUpdated are standalone - no additional sections needed */}
                            
                            {/* Actions */}
                            <div className="ghl-event-trigger-edit-actions">
                                <Button
                                    buttonType="destructive-button"
                                    size="large"
                                    text="Delete trigger"
                                    cta={() => {
                                        // Remove this trigger
                                        const newEvents = selectedEvents.filter(e => e !== eventValue);
                                        
                                        // Prepare updated state with cleared associated data for this trigger
                                        let updatedTags = selectedTags;
                                        let updatedOcPipelines = opportunityCreatedPipelines;
                                        let updatedOcStages = opportunityCreatedStages;
                                        let updatedOsuPipelines = opportunityStageUpdatedPipelines;
                                        let updatedOsuStages = opportunityStageUpdatedStages;
                                        
                                        // Clear associated data for this trigger type
                                        if (eventValue === 'ContactTagUpdated') {
                                            updatedTags = [];
                                            setAvailableTags(prev => prev.map(tag => ({ ...tag, selected: false })));
                                        } else if (eventValue === 'OpportunityCreated') {
                                            updatedOcPipelines = [];
                                            updatedOcStages = [];
                                        } else if (eventValue === 'OpportunityStageUpdated') {
                                            updatedOsuPipelines = [];
                                            updatedOsuStages = [];
                                        }
                                        
                                        // Update all state
                                        onEventsChange(newEvents);
                                        if (eventValue === 'ContactTagUpdated') {
                                            onTagsChange(updatedTags);
                                        } else if (eventValue === 'OpportunityCreated') {
                                            onOpportunityCreatedPipelinesChange(updatedOcPipelines);
                                            onOpportunityCreatedStagesChange(updatedOcStages);
                                        } else if (eventValue === 'OpportunityStageUpdated') {
                                            onOpportunityStageUpdatedPipelinesChange(updatedOsuPipelines);
                                            onOpportunityStageUpdatedStagesChange(updatedOsuStages);
                                        }
                                        
                                        // Automatically save to Firebase
                                        if (onSave) {
                                            setTimeout(() => {
                                                onSave(
                                                    newEvents,
                                                    updatedTags,
                                                    updatedOcPipelines,
                                                    updatedOcStages,
                                                    updatedOsuPipelines,
                                                    updatedOsuStages
                                                );
                                            }, 0);
                                        }
                                        
                                        // Exit edit mode if no triggers remain
                                        if (newEvents.length === 0) {
                                            toggleEditMode();
                                        }
                                        
                                        // Note: ContactCreated/ContactUpdated are auto-saved when added/changed,
                                        // so deletion is already handled by the auto-save above
                                    }}
                                    disabled={false}
                                    leftIcon={<HugeiconsIcon icon={Delete02Icon} color={colour.red03} size={16} />}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                                {shouldShowSaveButton(eventValue) && (
                                    <div className="ghl-event-trigger-save-button-wrapper">
                                        <Button
                                            buttonType="primary-button"
                                            size="large"
                                            text="Save changes"
                                            cta={() => {
                                                if (onSave) {
                                                    onSave(selectedEvents, selectedTags, opportunityCreatedPipelines, opportunityCreatedStages, opportunityStageUpdatedPipelines, opportunityStageUpdatedStages);
                                                }
                                                toggleEditMode();
                                            }}
                                            disabled={!isSaveButtonEnabled(eventValue)}
                                            leftIcon={null}
                                            leftIconClass=""
                                            rightIcon={null}
                                            rightIconClass=""
                                            alt=""
                                            standardIcon={false}
                                            badge={false}
                                            badgeVal={null}
                                            badgeBG={null}
                                            buttonRef={null}
                                            spinner={false}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                
                {/* Add trigger dropdown */}
                <div className="ghl-event-trigger-add-trigger-container">
                    <Dropdown
                        open={addTriggerDropdownOpen}
                        icon={<HugeiconsIcon icon={PlusSignIcon} color={colour.gray06} size={16} />}
                        label="Add trigger"
                        disabled={!eventOptions || eventOptions.length === 0 || (selectedEvents && selectedEvents.length >= eventOptions.length)}
                        options={eventOptions
                            .filter(opt => !selectedEvents?.includes(opt.value))
                            .map(opt => ({
                                label: opt.label,
                                id: opt.value,
                                selected: false
                            }))}
                        multiselect={false}
                        cta={(option) => {
                            const newEvents = [...(selectedEvents || []), option.id];
                            onEventsChange(newEvents);
                            setAddTriggerDropdownOpen(false);
                            
                            // Auto-save ContactCreated and ContactUpdated
                            if (option.id === 'ContactCreated' || option.id === 'ContactUpdated') {
                                autoSaveContactEvents(newEvents);
                            }
                        }}
                        toggleDropdown={() => {
                            setAddTriggerDropdownOpen(prev => !prev);
                        }}
                        sidealign="left"
                        topalign="bottom"
                        indexSelect={false}
                        selectedIndex={null}
                    />
                </div>
            </div>
        );
    };

    // Show nothing while initially loading
    if (initialLoading && (!selectedEvents || selectedEvents.length === 0)) {
        return null;
    }

    return (
        <div className="ghl-event-trigger-container">
            {editMode ? renderEditView() : renderReadOnlyView()}
        </div>
    );
}

export default GHLEventTrigger;
